import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";

interface SpectatorTicket {
  name: string;
  price: number;
  description?: string;
  quantity_available?: number;
  is_active: boolean;
}

interface SpectatorTicketsTabProps {
  tickets: SpectatorTicket[];
  onUpdate: (tickets: SpectatorTicket[]) => void;
}

export default function SpectatorTicketsTab({ tickets, onUpdate }: SpectatorTicketsTabProps) {
  const addTicket = () => {
    onUpdate([
      ...tickets,
      { name: "", price: 0, description: "", quantity_available: 100, is_active: true },
    ]);
  };

  const updateTicket = (index: number, field: keyof SpectatorTicket, value: any) => {
    const updated = [...tickets];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate(updated);
  };

  const removeTicket = (index: number) => {
    onUpdate(tickets.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Spectator Tickets</CardTitle>
            <Button onClick={addTicket} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Ticket Type
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No spectator tickets configured. Click "Add Ticket Type" to create ticket options.
            </p>
          ) : (
            tickets.map((ticket, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label>Ticket Name</Label>
                        <Input
                          placeholder="e.g., Adult, Child, Senior"
                          value={ticket.name}
                          onChange={(e) => updateTicket(index, "name", e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Price (cents)</Label>
                          <Input
                            type="number"
                            placeholder="1000"
                            value={ticket.price || ""}
                            onChange={(e) => updateTicket(index, "price", parseInt(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label>Quantity Available</Label>
                          <Input
                            type="number"
                            placeholder="100"
                            value={ticket.quantity_available || ""}
                            onChange={(e) => updateTicket(index, "quantity_available", parseInt(e.target.value) || undefined)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description (optional)</Label>
                        <Textarea
                          placeholder="Ticket description..."
                          value={ticket.description || ""}
                          onChange={(e) => updateTicket(index, "description", e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Label htmlFor={`active-${index}`}>Active</Label>
                        <Switch
                          id={`active-${index}`}
                          checked={ticket.is_active}
                          onCheckedChange={(checked) => updateTicket(index, "is_active", checked)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTicket(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
